import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import PeriodicElement from './PeriodicElement';
import { FaGithub, FaLinkedin, FaInstagram, FaChevronDown, FaDownload } from 'react-icons/fa';

const Hero = () => {
  const [showSmoke, setShowSmoke] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowSmoke(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const socialLinks = [
    { icon: FaGithub, href: 'https://github.com/jitenmahajan', label: 'GitHub' },
    { icon: FaLinkedin, href: 'https://www.linkedin.com/in/jiten-mahajan-2246351aa?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app', label: 'LinkedIn' },
    { icon: FaInstagram, href: 'https://instagram.com/jiten.eth', label: 'Instagram' },
  ];

  const handleDownloadResume = () => {
    const link = document.createElement('a');
    link.href = '/JitenMahajan.pdf';
    link.download = 'JitenMahajan_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section id="home" className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden pt-16">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-900 to-black" />

      {/* Chemical Formula Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 text-6xl font-bold text-green-500">C₁₀H₁₅N</div>
        <div className="absolute top-40 right-20 text-4xl font-bold text-green-500">CH₃</div>
        <div className="absolute bottom-40 left-20 text-5xl font-bold text-green-500">NH₂</div>
        <div className="absolute bottom-20 right-10 text-3xl font-bold text-green-500">H₂O</div>
      </div>

      {/* Smoke Effect */}
      {showSmoke && (
        <motion.div
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 0, y: -100 }}
          transition={{ duration: 3 }}
          className="absolute inset-0 bg-gradient-to-t from-transparent via-green-900/30 to-green-500/20 pointer-events-none"
        />
      )}

      {/* Floating particles */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-green-500 rounded-full opacity-30"
          initial={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight
          }}
          animate={{
            y: [null, Math.random() * -200],
            opacity: [0.3, 0]
          }}
          transition={{
            duration: Math.random() * 5 + 3,
            repeat: Infinity,
            repeatDelay: Math.random() * 2
          }}
        />
      ))}

      <div className="relative z-10 text-center px-4">
        {/* Name in Periodic Table Style */}
        <motion.div
          className="flex justify-center flex-wrap mb-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <PeriodicElement symbol="Ji" name="Jiten" number={26} delay={0.1} />
          <PeriodicElement symbol="Te" name="Tellurium" number={52} isHighlighted delay={0.2} />
          <PeriodicElement symbol="N" name="Nitrogen" number={7} delay={0.3} />
        </motion.div>

        <motion.div
          className="flex justify-center flex-wrap mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <PeriodicElement symbol="Ma" name="Mahajan" number={12} delay={0.4} />
          <PeriodicElement symbol="H" name="Hydrogen" number={1} isHighlighted delay={0.5} />
          <PeriodicElement symbol="Aj" name="Adjust" number={47} delay={0.6} />
          <PeriodicElement symbol="An" name="Anode" number={79} delay={0.7} />
        </motion.div>

        {/* Tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mb-8"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-bb-green-light font-bold mb-4">
            Full-Stack Developer
          </h2>
          <motion.p
            className="text-gray-400 text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <span className="text-bb-green">"Say my name."</span> I cook clean code, not just any code.
            <br />
            <span className="text-sm text-gray-500">99.1% Pure Code Quality</span>
          </motion.p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 }}
          className="flex flex-wrap justify-center gap-4 mb-8"
        >
          <motion.a
            href="#projects"
            className="px-8 py-3 bg-bb-green text-white font-bold rounded-lg hover:bg-bb-green-light transition-all shadow-lg shadow-green-900/50"
            whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(0, 200, 83, 0.5)" }}
            whileTap={{ scale: 0.95 }}
          >
            View My Lab Work
          </motion.a>
          <motion.button
            onClick={handleDownloadResume}
            className="px-8 py-3 border-2 border-bb-green text-bb-green font-bold rounded-lg hover:bg-bb-green hover:text-white transition-all flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaDownload />
            Download Resume
          </motion.button>
          <motion.a
            href="#contact"
            className="px-8 py-3 border-2 border-bb-green text-bb-green font-bold rounded-lg hover:bg-bb-green hover:text-white transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Let's Cook Together
          </motion.a>
        </motion.div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="flex justify-center gap-6"
        >
          {socialLinks.map((social, index) => (
            <motion.a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-bb-green-light transition-colors"
              whileHover={{ scale: 1.2, y: -3 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2 + index * 0.1 }}
            >
              <social.icon size={28} />
            </motion.a>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.a
          href="#about"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="text-bb-green-light"
        >
          <FaChevronDown size={32} />
        </motion.a>
      </motion.div>
    </section>
  );
};

export default Hero;
